import { apiGet, apiPost, apiPatch, apiDelete } from "../lib/api-client";

// Types are defined locally rather than in @atlas/shared: this feature's
// scope is db + api + web only, shared/mobile are out of scope (another
// agent owns them in parallel).

export type ItemType = "peak" | "landmark" | "trail";

export interface UserCollectionSummary {
  id: string;
  name: string;
  description: string | null;
  createdAt: string;
  itemCount: number;
  completedCount: number;
  pct: number;
}

export interface UserCollectionItem {
  id: string;
  itemType: ItemType;
  name: string;
  slug: string;
  collected: boolean;
  addedAt: string;
  elevationM: number | null;
  latitude: number | null;
  longitude: number | null;
  category: string | null;
  distanceKm: number | null;
  elevationGainM: number | null;
  totalSections: number | null;
  completedSections: number | null;
}

export interface UserCollectionDetail {
  collection: { id: string; name: string; description: string | null; createdAt: string };
  items: UserCollectionItem[];
}

export interface SearchResultItem {
  itemType: ItemType;
  id: string;
  name: string;
  slug: string;
}

export function fetchUserCollections(): Promise<{ collections: UserCollectionSummary[] }> {
  return apiGet("/user-collections");
}

export function fetchUserCollection(id: string): Promise<UserCollectionDetail> {
  return apiGet(`/user-collections/${id}`);
}

export function createUserCollection(params: {
  name: string;
  description?: string;
}): Promise<{ collection: UserCollectionSummary }> {
  return apiPost("/user-collections", params);
}

export function updateUserCollection(
  id: string,
  params: { name?: string; description?: string | null },
): Promise<{ collection: { id: string; name: string; description: string | null; createdAt: string } }> {
  return apiPatch(`/user-collections/${id}`, params);
}

export function deleteUserCollection(id: string): Promise<{ ok: boolean }> {
  return apiDelete(`/user-collections/${id}`);
}

export function addUserCollectionItem(
  id: string,
  params: { itemType: ItemType; itemId: string },
): Promise<{ item: { id: string; itemType: ItemType; itemId: string } }> {
  return apiPost(`/user-collections/${id}/items`, params);
}

export function removeUserCollectionItem(
  id: string,
  itemType: ItemType,
  itemId: string,
): Promise<{ ok: boolean }> {
  return apiDelete(`/user-collections/${id}/items/${itemType}/${itemId}`);
}

export function searchFeatures(q: string): Promise<{ items: SearchResultItem[] }> {
  return apiGet(`/user-collections/search?q=${encodeURIComponent(q)}`);
}
